import { useState, useMemo, lazy, Suspense } from "react"
import { DataGrid, type DataGridColumn } from "@/components/ui/DataGrid"
import { useAuthStore } from "@/stores/authStore"
import { Card } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"
import { Input } from "@/components/ui/Input"
import {
  useArticles, useCategories, useCreateArticle, useUpdateArticle, useDeleteArticle,
  usePublishArticle, useApproveArticle, useCreateCategory, useUpdateCategory, useDeleteCategory,
  useSocialPosts, useSocialAccounts, useBulkCreateSocialPosts,
  type Article, type Category, type SocialAccount,
} from "@/hooks/useContentHub"
import {
  FileText, Tag, Share2, Plus, Search, Edit2, Trash2, Eye, Check, X,
  ChevronLeft, ChevronRight, Loader2, Star, CheckCircle, Clock, AlertTriangle,
  XCircle, Send, Shield, Image as ImageIcon, BookOpen, Users,
} from "lucide-react"

const RichTextEditor = lazy(() => import("@/components/ui/RichTextEditor"))

// ─── Status Configs ──────────────────────────────────────────────────────
const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: "Rascunho", color: "text-gray-600 dark:text-gray-400", bg: "bg-gray-100 dark:bg-gray-800" },
  pending_review: { label: "Pendente", color: "text-yellow-700 dark:text-yellow-300", bg: "bg-yellow-100 dark:bg-yellow-900/40" },
  approved: { label: "Aprovado", color: "text-blue-700 dark:text-blue-300", bg: "bg-blue-100 dark:bg-blue-900/40" },
  published: { label: "Publicado", color: "text-green-700 dark:text-green-300", bg: "bg-green-100 dark:bg-green-900/40" },
  archived: { label: "Arquivado", color: "text-red-700 dark:text-red-300", bg: "bg-red-100 dark:bg-red-900/40" },
}

const APPROVAL_CFG: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  pending: { label: "Pendente", color: "text-yellow-700 dark:text-yellow-300", bg: "bg-yellow-100 dark:bg-yellow-900/40", icon: Clock },
  approved: { label: "Aprovado", color: "text-green-700 dark:text-green-300", bg: "bg-green-100 dark:bg-green-900/40", icon: CheckCircle },
  revision: { label: "Revisar", color: "text-orange-700 dark:text-orange-300", bg: "bg-orange-100 dark:bg-orange-900/40", icon: AlertTriangle },
  rejected: { label: "Desaprovado", color: "text-red-700 dark:text-red-300", bg: "bg-red-100 dark:bg-red-900/40", icon: XCircle },
}

const PLATFORM_COLORS: Record<string, { bg: string; label: string; dot: string }> = {
  linkedin: { bg: "bg-[#0A66C2]", label: "LinkedIn", dot: "#0A66C2" },
  instagram: { bg: "bg-[#E4405F]", label: "Instagram", dot: "#E4405F" },
  facebook: { bg: "bg-[#1877F2]", label: "Facebook", dot: "#1877F2" },
  twitter: { bg: "bg-black dark:bg-white dark:text-black", label: "X", dot: "#000" },
}

type Tab = "articles" | "categories" | "social"

// ─── StatusBadge ──────────────────────────────────────────────────────
function StatusBadge({ status, type }: { status: string; type: "status" | "approval" }) {
  const cfg = type === "status" ? STATUS_CFG[status] : APPROVAL_CFG[status]
  if (!cfg) return <span className="text-xs text-muted-foreground">{status}</span>
  const Icon = type === "approval" ? (APPROVAL_CFG[status]?.icon || Clock) : null
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color} ${cfg.bg}`}>
      {Icon && <Icon className="h-3 w-3" />} {cfg.label}
    </span>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────
export default function ContentHubPage() {
  const { user } = useAuthStore()
  const isManager = (user?.role?.hierarchy_level ?? 0) >= 70

  const [tab, setTab] = useState<Tab>("articles")
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [approvalFilter, setApprovalFilter] = useState("")

  // Article modals
  const [showForm, setShowForm] = useState(false)
  const [editArticle, setEditArticle] = useState<Article | null>(null)
  const [previewArticle, setPreviewArticle] = useState<Article | null>(null)
  const [approveArticle, setApproveArticle] = useState<Article | null>(null)

  // Category modals
  const [showCatForm, setShowCatForm] = useState(false)
  const [editCategory, setEditCategory] = useState<Category | null>(null)

  const { data: articlesData, isLoading: loadingArticles } = useArticles({ page: 1, perPage: 500 })
  const { data: categoriesData } = useCategories()
  const { data: socialData } = useSocialPosts({ page: 1, perPage: 20 })

  const allArticles = articlesData?.items || []
  const articles = useMemo(() => {
    let items = allArticles
    if (search) { const s = search.toLowerCase(); items = items.filter(a => a.title.toLowerCase().includes(s)) }
    if (statusFilter) items = items.filter(a => a.status === statusFilter)
    if (approvalFilter) items = items.filter(a => (a.approval_status || 'pending') === approvalFilter)
    return items
  }, [allArticles, search, statusFilter, approvalFilter])
  const categories = categoriesData?.items || []
  const socialPosts = socialData?.items || []
  

  const tabs = [
    { id: "articles" as Tab, label: "Artigos", icon: FileText, count: articlesData?.total },
    { id: "categories" as Tab, label: "Categorias", icon: Tag, count: categoriesData?.total },
    { id: "social" as Tab, label: "Social Posts", icon: Share2, count: socialData?.total },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Content Hub</h1>
          <p className="text-muted-foreground text-sm">Gerencie artigos, categorias e publicações sociais</p>
        </div>
        {tab === "articles" && (
          <Button onClick={() => { setEditArticle(null); setShowForm(true) }}>
            <Plus className="h-4 w-4 mr-1" /> Novo Artigo
          </Button>
        )}
        {tab === "categories" && (
          <Button onClick={() => { setEditCategory(null); setShowCatForm(true) }}>
            <Plus className="h-4 w-4 mr-1" /> Nova Categoria
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setPage(1) }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
            {t.count != null && <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab: Articles */}
      {tab === "articles" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Buscar artigos..." value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} />
            </div>
            <select className="border rounded-md px-3 py-2 text-sm bg-background" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }}>
              <option value="">Todos os Status</option>
              {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select className="border rounded-md px-3 py-2 text-sm bg-background" value={approvalFilter} onChange={e => { setApprovalFilter(e.target.value); setPage(1) }}>
              <option value="">Todas Aprovações</option>
              {Object.entries(APPROVAL_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          {/* Table */}
          <Card className="overflow-hidden">
            <DataGrid
              gridId="content-articles"
              columns={[
                { id: 'title', header: 'Artigo', accessor: (a: Article) => <div className="flex items-center gap-3">{a.cover_image ? <img src={a.cover_image} className="w-10 h-10 rounded object-cover flex-shrink-0" alt="" /> : <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0"><FileText className="h-4 w-4 text-muted-foreground" /></div>}<div className="min-w-0"><p className="font-medium truncate max-w-[250px]">{a.title}</p>{a.featured && <span className="text-xs text-yellow-600"><Star className="h-3 w-3 inline mr-0.5" />Destaque</span>}</div></div>, sortValue: (a) => a.title, filterValue: (a) => a.title },
                { id: 'category', header: 'Categoria', accessor: (a: Article) => <span className="text-muted-foreground">{a.category_name || "\u2014"}</span>, sortValue: (a) => a.category_name || '', filterValue: (a) => a.category_name || '' },
                { id: 'status', header: 'Status', accessor: (a: Article) => <StatusBadge status={a.status} type="status" />, sortValue: (a) => a.status, filterValue: (a) => STATUS_CFG[a.status]?.label || a.status },
                { id: 'approval', header: 'Aprovação', accessor: (a: Article) => <StatusBadge status={a.approval_status || "pending"} type="approval" />, sortValue: (a) => a.approval_status || 'pending', filterValue: (a) => APPROVAL_CFG[a.approval_status || 'pending']?.label || '' },
                { id: 'views', header: 'Views', accessor: (a: Article) => <span className="text-muted-foreground">{a.view_count}</span>, sortValue: (a) => a.view_count, filterValue: (a) => String(a.view_count), align: 'right' as const },
                { id: 'author', header: 'Autor', accessor: (a: Article) => <span className="text-muted-foreground text-xs">{a.author_name || "\u2014"}</span>, sortValue: (a) => a.author_name || '', filterValue: (a) => a.author_name || '' },
                { id: 'date', header: 'Data', accessor: (a: Article) => <span className="text-muted-foreground text-xs">{new Date(a.created_at).toLocaleDateString("pt-BR")}</span>, sortValue: (a) => new Date(a.created_at).getTime(), filterValue: (a) => new Date(a.created_at).toLocaleDateString("pt-BR") },
                { id: 'actions', header: 'Ações', sortable: false, filterable: false, align: 'right' as const, accessor: (a: Article) => <div className="flex items-center justify-end gap-1" onClick={(e: any) => e.stopPropagation()}><button onClick={() => setPreviewArticle(a)} className="p-1.5 rounded hover:bg-muted" title="Visualizar"><Eye className="h-3.5 w-3.5" /></button><button onClick={() => { setEditArticle(a); setShowForm(true) }} className="p-1.5 rounded hover:bg-muted" title="Editar"><Edit2 className="h-3.5 w-3.5" /></button>{isManager && a.approval_status !== "approved" && a.status !== "published" && <button onClick={() => setApproveArticle(a)} className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600" title="Aprovar"><Shield className="h-3.5 w-3.5" /></button>}{a.approval_status === "approved" && a.status !== "published" && <PublishButton articleId={a.id} />}<DeleteArticleButton articleId={a.id} title={a.title} /></div> },
              ] as DataGridColumn<Article>[]}
              data={articles}
              isLoading={loadingArticles}
              emptyMessage="Nenhum artigo encontrado"
              pageSize={20}
            />
          </Card>
        </div>
      )}

      {/* Tab: Categories */}
      {tab === "categories" && (
        <Card className="overflow-hidden">
          <DataGrid
            gridId="content-categories"
            columns={[
              { id: 'name', header: 'Categoria', accessor: (c: Category) => <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || "#6b7280" }} /><span className="font-medium">{c.name}</span></div>, sortValue: (c) => c.name, filterValue: (c) => c.name },
              { id: 'slug', header: 'Slug', accessor: (c: Category) => <span className="text-muted-foreground text-xs">{c.slug}</span>, sortValue: (c) => c.slug, filterValue: (c) => c.slug },
              { id: 'description', header: 'Descri\u00e7\u00e3o', accessor: (c: Category) => <span className="text-muted-foreground text-xs truncate max-w-[200px] block">{c.description || "\u2014"}</span>, sortValue: (c) => c.description || '', filterValue: (c) => c.description || '' },
              { id: 'count', header: 'Artigos', accessor: (c: Category) => <span>{c.article_count}</span>, sortValue: (c) => c.article_count, filterValue: (c) => String(c.article_count), align: 'right' as const },
              { id: 'actions', header: 'A\u00e7\u00f5es', sortable: false, filterable: false, align: 'right' as const, accessor: (c: Category) => <div className="flex items-center justify-end gap-1" onClick={(e: any) => e.stopPropagation()}><button onClick={() => { setEditCategory(c); setShowCatForm(true) }} className="p-1.5 rounded hover:bg-muted"><Edit2 className="h-3.5 w-3.5" /></button><DeleteCategoryButton id={c.id} name={c.name} /></div> },
            ] as DataGridColumn<Category>[]}
            data={categories}
            emptyMessage="Nenhuma categoria"
            pageSize={20}
          />
        </Card>
      )}

      {/* Tab: Social Posts */}
      {tab === "social" && (
        <Card className="overflow-hidden">
          <DataGrid
            gridId="content-social-posts"
            columns={[
              { id: 'content', header: 'Conte\u00fado', accessor: (p: any) => <span className="truncate max-w-[300px] block">{p.content}</span>, sortValue: (p: any) => p.content, filterValue: (p: any) => p.content },
              { id: 'platform', header: 'Plataforma', accessor: (p: any) => { const pl = PLATFORM_COLORS[p.platform] || { bg: "bg-gray-500", label: p.platform }; return <span className={`text-white text-xs px-2 py-0.5 rounded ${pl.bg}`}>{pl.label}</span> }, sortValue: (p: any) => p.platform, filterValue: (p: any) => PLATFORM_COLORS[p.platform]?.label || p.platform },
              { id: 'status', header: 'Status', accessor: (p: any) => <StatusBadge status={p.status} type="status" />, sortValue: (p: any) => p.status, filterValue: (p: any) => STATUS_CFG[p.status]?.label || p.status },
              { id: 'impressions', header: 'Impress\u00f5es', accessor: (p: any) => <span>{p.impressions.toLocaleString()}</span>, sortValue: (p: any) => p.impressions, filterValue: (p: any) => String(p.impressions), align: 'right' as const },
              { id: 'engagements', header: 'Engajamento', accessor: (p: any) => <span>{p.engagements.toLocaleString()}</span>, sortValue: (p: any) => p.engagements, filterValue: (p: any) => String(p.engagements), align: 'right' as const },
              { id: 'scheduled', header: 'Agendado', accessor: (p: any) => <span className="text-xs text-muted-foreground">{p.scheduled_at ? new Date(p.scheduled_at).toLocaleDateString("pt-BR") : "\u2014"}</span>, sortValue: (p: any) => p.scheduled_at ? new Date(p.scheduled_at).getTime() : 0, filterValue: (p: any) => p.scheduled_at ? new Date(p.scheduled_at).toLocaleDateString("pt-BR") : '' },
              { id: 'published', header: 'Publicado', accessor: (p: any) => <span className="text-xs text-muted-foreground">{p.published_at ? new Date(p.published_at).toLocaleDateString("pt-BR") : "\u2014"}</span>, sortValue: (p: any) => p.published_at ? new Date(p.published_at).getTime() : 0, filterValue: (p: any) => p.published_at ? new Date(p.published_at).toLocaleDateString("pt-BR") : '' },
            ]}
            data={socialPosts}
            emptyMessage="Nenhum post social"
            pageSize={20}
          />
        </Card>
      )}

      {/* Modals */}
      {showForm && <ArticleFormModal article={editArticle} categories={categories} onClose={() => setShowForm(false)} />}
      {previewArticle && <PreviewModal article={previewArticle} onClose={() => setPreviewArticle(null)} />}
      {approveArticle && <ApprovalModal article={approveArticle} onClose={() => setApproveArticle(null)} />}
      {showCatForm && <CategoryFormModal category={editCategory} onClose={() => setShowCatForm(false)} />}
    </div>
  )
}

// ─── Publish Button ──────────────────────────────────────────────────────
function PublishButton({ articleId }: { articleId: string }) {
  const pub = usePublishArticle()
  return (
    <button onClick={() => pub.mutate(articleId)} disabled={pub.isPending}
      className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600" title="Publicar">
      {pub.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
    </button>
  )
}

function DeleteArticleButton({ articleId, title }: { articleId: string; title: string }) {
  const del = useDeleteArticle()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500" title="Excluir">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
      <ConfirmDialog
        open={open}
        title="Excluir artigo?"
        message={`O artigo "${title}" será removido. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={del.isPending}
        onConfirm={async () => { await del.mutateAsync(articleId); setOpen(false) }}
        onCancel={() => setOpen(false)}
      />
    </>
  )
}

function DeleteCategoryButton({ id, name }: { id: string; name: string }) {
  const del = useDeleteCategory()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500" title="Excluir">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
      <ConfirmDialog
        open={open}
        title="Excluir categoria?"
        message={`A categoria "${name}" será removida. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={del.isPending}
        onConfirm={async () => { await del.mutateAsync(id); setOpen(false) }}
        onCancel={() => setOpen(false)}
      />
    </>
  )
}

// ─── Social Account Selector (used in ArticleFormModal) ──────────────────
function SocialAccountSelector({ selectedIds, onChange }: { selectedIds: string[]; onChange: (ids: string[]) => void }) {
  const { data: socialData, isLoading } = useSocialAccounts()
  const accounts = socialData?.items || []

  const grouped = useMemo(() => {
    const map: Record<string, SocialAccount[]> = {}
    for (const a of accounts) {
      if (!map[a.platform]) map[a.platform] = []
      map[a.platform].push(a)
    }
    return map
  }, [accounts])

  function toggle(id: string) {
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id])
  }

  function togglePlatform(platform: string) {
    const platformIds = (grouped[platform] || []).map(a => a.id)
    const allSelected = platformIds.every(id => selectedIds.includes(id))
    if (allSelected) {
      onChange(selectedIds.filter(id => !platformIds.includes(id)))
    } else {
      onChange([...new Set([...selectedIds, ...platformIds])])
    }
  }

  if (isLoading) return <div className="text-xs text-muted-foreground py-2"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" />Carregando perfis...</div>
  if (accounts.length === 0) return <p className="text-xs text-muted-foreground py-2">Nenhum perfil social conectado. Configure em Configurações &gt; Redes Sociais.</p>

  return (
    <div className="space-y-3">
      {Object.entries(grouped).map(([platform, accs]) => {
        const platCfg = PLATFORM_COLORS[platform] || { bg: "bg-gray-500", label: platform, dot: "#6b7280" }
        const platformIds = accs.map(a => a.id)
        const allSelected = platformIds.every(id => selectedIds.includes(id))
        const someSelected = platformIds.some(id => selectedIds.includes(id))

        return (
          <div key={platform} className="border rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 cursor-pointer" onClick={() => togglePlatform(platform)}>
              <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected && !allSelected }} readOnly className="rounded" />
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: platCfg.dot }} />
              <span className="text-sm font-medium">{platCfg.label}</span>
              <span className="text-xs text-muted-foreground ml-auto">{accs.length} perfil{accs.length > 1 ? "s" : ""}</span>
            </div>
            <div className="divide-y">
              {accs.map(a => (
                <label key={a.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/20 transition-colors">
                  <input type="checkbox" checked={selectedIds.includes(a.id)} onChange={() => toggle(a.id)} className="rounded" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm">{a.account_name}</span>
                    <span className="text-xs text-muted-foreground ml-1.5">@{a.account_id}</span>
                  </div>
                  {a.owner_name && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Users className="h-3 w-3" />{a.owner_name}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Article Form Modal ──────────────────────────────────────────────────
function ArticleFormModal({ article, categories, onClose }: { article: Article | null; categories: Category[]; onClose: () => void }) {
  const isEdit = !!article
  const create = useCreateArticle()
  const update = useUpdateArticle()
  const createCat = useCreateCategory()
  const bulkPost = useBulkCreateSocialPosts()

  const [title, setTitle] = useState(article?.title || "")
  const [slug, setSlug] = useState(article?.slug || "")
  const [content, setContent] = useState(article?.content || "")
  const [excerpt, setExcerpt] = useState(article?.excerpt || "")
  const [coverImage, setCoverImage] = useState(article?.cover_image || "")
  const [categoryId, setCategoryId] = useState(article?.category_id || "")
  const [metaTitle, setMetaTitle] = useState(article?.meta_title || "")
  const [metaDesc, setMetaDesc] = useState(article?.meta_description || "")
  const [featured, setFeatured] = useState(article?.featured || false)
  const [showNewCat, setShowNewCat] = useState(false)
  const [newCatName, setNewCatName] = useState("")
  const [newCatColor, setNewCatColor] = useState("#3b82f6")
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([])
  const [socialContent, setSocialContent] = useState("")

  const saving = create.isPending || update.isPending

  function autoSlug(t: string) {
    return t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
  }

  async function handleSave() {
    const data: Record<string, unknown> = { title, slug: slug || autoSlug(title), content, excerpt: excerpt || undefined, cover_image: coverImage || undefined, category_id: categoryId || undefined, meta_title: metaTitle || undefined, meta_description: metaDesc || undefined, featured }
    let savedArticle: Article | undefined
    if (isEdit) {
      savedArticle = await update.mutateAsync({ id: article!.id, data })
    } else {
      savedArticle = await create.mutateAsync(data)
    }

    // Create social posts if accounts selected
    if (selectedAccountIds.length > 0 && savedArticle?.id) {
      const postContent = socialContent.trim() || `${title}${excerpt ? " - " + excerpt : ""}`
      await bulkPost.mutateAsync({
        content: postContent,
        article_id: savedArticle.id,
        account_ids: selectedAccountIds,
        link_url: coverImage || undefined,
      })
    }

    onClose()
  }

  async function handleCreateCategory() {
    if (!newCatName.trim()) return
    const cat = await createCat.mutateAsync({ name: newCatName, color: newCatColor, slug: autoSlug(newCatName) })
    if (cat?.id) setCategoryId(cat.id)
    setShowNewCat(false)
    setNewCatName("")
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 px-4 bg-black/50" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-background z-10">
          <h2 className="text-lg font-semibold">{isEdit ? "Editar Artigo" : "Novo Artigo"}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Title + Slug */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Título *</label>
              <Input className="mt-1" value={title} onChange={e => { setTitle(e.target.value); if (!isEdit) setSlug(autoSlug(e.target.value)) }} placeholder="Título do artigo" />
            </div>
            <div>
              <label className="text-sm font-medium">Slug</label>
              <Input className="mt-1" value={slug} onChange={e => setSlug(e.target.value)} placeholder="titulo-do-artigo" />
            </div>
          </div>

          {/* Category with + button */}
          <div>
            <label className="text-sm font-medium">Categoria</label>
            <div className="flex gap-2 mt-1">
              <select className="flex-1 border rounded-md px-3 py-2 text-sm bg-background" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                <option value="">Selecione...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowNewCat(!showNewCat)} title="Nova categoria">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {showNewCat && (
              <div className="mt-2 p-3 border rounded-lg bg-muted/30 flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-xs font-medium">Nome</label>
                  <Input className="mt-0.5" value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="Nova categoria" />
                </div>
                <div>
                  <label className="text-xs font-medium">Cor</label>
                  <input type="color" className="mt-0.5 block w-10 h-9 rounded border cursor-pointer" value={newCatColor} onChange={e => setNewCatColor(e.target.value)} />
                </div>
                <Button size="sm" onClick={handleCreateCategory} disabled={createCat.isPending}>
                  {createCat.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowNewCat(false)}><X className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </div>

          {/* Cover image */}
          <div>
            <label className="text-sm font-medium">Imagem de Capa</label>
            <div className="mt-1 flex gap-3 items-start">
              <Input className="flex-1" value={coverImage} onChange={e => setCoverImage(e.target.value)} placeholder="https://exemplo.com/imagem.jpg" />
              {coverImage && <img src={coverImage} className="w-20 h-14 rounded object-cover border" alt="preview" onError={e => (e.currentTarget.style.display = "none")} />}
            </div>
          </div>

          {/* Rich Text Editor */}
          <div>
            <label className="text-sm font-medium">Conteúdo *</label>
            <div className="mt-1">
              <Suspense fallback={<div className="border rounded-lg p-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Carregando editor...</div>}>
                <RichTextEditor value={content} onChange={setContent} />
              </Suspense>
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="text-sm font-medium">Resumo</label>
            <textarea className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background resize-none" rows={3} value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Breve resumo do artigo..." />
          </div>

          {/* Social Publishing */}
          <div className="border rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-muted/30 border-b">
              <Share2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Publicação nas Redes Sociais</span>
              {selectedAccountIds.length > 0 && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{selectedAccountIds.length} selecionado{selectedAccountIds.length > 1 ? "s" : ""}</span>
              )}
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-muted-foreground">Selecione os perfis onde o artigo será publicado ao salvar. Se nenhum perfil for selecionado, nenhum post social será criado.</p>
              <SocialAccountSelector selectedIds={selectedAccountIds} onChange={setSelectedAccountIds} />
              {selectedAccountIds.length > 0 && (
                <div>
                  <label className="text-xs font-medium">Texto do post social (opcional)</label>
                  <textarea
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background resize-none"
                    rows={2}
                    value={socialContent}
                    onChange={e => setSocialContent(e.target.value)}
                    placeholder={`Padrão: "${title}${excerpt ? " - " + excerpt.substring(0, 50) + "..." : ""}"`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* SEO */}
          <details className="group">
            <summary className="text-sm font-medium cursor-pointer text-muted-foreground hover:text-foreground">SEO & Meta</summary>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium">Meta Title</label>
                <Input className="mt-1" value={metaTitle} onChange={e => setMetaTitle(e.target.value)} placeholder={title} />
              </div>
              <div>
                <label className="text-xs font-medium">Meta Description</label>
                <Input className="mt-1" value={metaDesc} onChange={e => setMetaDesc(e.target.value)} placeholder="Descrição para mecanismos de busca" />
              </div>
            </div>
          </details>

          {/* Featured */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={featured} onChange={e => setFeatured(e.target.checked)} className="rounded" />
            <Star className="h-4 w-4 text-yellow-500" />
            <span className="text-sm">Artigo em destaque</span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t sticky bottom-0 bg-background">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !title.trim() || !content.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            {isEdit ? "Salvar" : "Criar Artigo"}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Preview Modal ──────────────────────────────────────────────────────
function PreviewModal({ article, onClose }: { article: Article; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-4 px-4 bg-black/60" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        {/* Cover */}
        {article.cover_image ? (
          <div className="relative h-64 md:h-80 overflow-hidden rounded-t-xl">
            <img src={article.cover_image} className="w-full h-full object-cover" alt="" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              {article.category_name && <span className="inline-block bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium mb-3">{article.category_name}</span>}
              <h1 className="text-2xl md:text-3xl font-bold leading-tight">{article.title}</h1>
            </div>
          </div>
        ) : (
          <div className="p-6 border-b">
            {article.category_name && <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium mb-3">{article.category_name}</span>}
            <h1 className="text-2xl md:text-3xl font-bold">{article.title}</h1>
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-4 px-6 py-4 border-b text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{(article.author_name || "?")[0]}</div>
            <span>{article.author_name || "Anônimo"}</span>
          </div>
          <span>{new Date(article.created_at).toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })}</span>
          <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" />{article.read_time_minutes} min</span>
          <div className="ml-auto flex gap-3">
            <span>{article.view_count} views</span>
            <span>{article.like_count} likes</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="prose dark:prose-invert prose-sm sm:prose max-w-none" dangerouslySetInnerHTML={{ __html: article.content }} />
        </div>

        {/* Close */}
        <div className="sticky bottom-0 p-4 border-t bg-background flex justify-end">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>
  )
}

// ─── Approval Modal ──────────────────────────────────────────────────────
function ApprovalModal({ article, onClose }: { article: Article; onClose: () => void }) {
  const approve = useApproveArticle()
  const [status, setStatus] = useState<string>("approved")
  const [comment, setComment] = useState("")

  async function handleSubmit() {
    await approve.mutateAsync({ id: article.id, status, comment: comment || undefined })
    onClose()
  }

  const needsComment = status === "revision" || status === "rejected"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-5 border-b">
          <h2 className="text-lg font-semibold">Aprovar Artigo</h2>
          <p className="text-sm text-muted-foreground mt-1 truncate">{article.title}</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="space-y-2">
            {([
              { value: "approved", label: "Aprovar", desc: "Artigo pronto para publicação", icon: CheckCircle, color: "text-green-600" },
              { value: "revision", label: "Solicitar Revisão", desc: "Solicitar alterações ao autor", icon: AlertTriangle, color: "text-orange-500" },
              { value: "rejected", label: "Rejeitar", desc: "Artigo não aprovado", icon: XCircle, color: "text-red-500" },
            ] as const).map(opt => (
              <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${status === opt.value ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}>
                <input type="radio" name="approval" value={opt.value} checked={status === opt.value} onChange={e => setStatus(e.target.value)} className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <opt.icon className={`h-4 w-4 ${opt.color}`} />
                    <span className="font-medium text-sm">{opt.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium">Comentário {needsComment && <span className="text-red-500">*</span>}</label>
            <textarea className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background resize-none" rows={3} value={comment} onChange={e => setComment(e.target.value)} placeholder={needsComment ? "Informe o motivo..." : "Comentário opcional..."} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={approve.isPending || (needsComment && !comment.trim())}>
            {approve.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Confirmar
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Category Form Modal ──────────────────────────────────────────────────
function CategoryFormModal({ category, onClose }: { category: Category | null; onClose: () => void }) {
  const isEdit = !!category
  const create = useCreateCategory()
  const update = useUpdateCategory()

  const [name, setName] = useState(category?.name || "")
  const [slug, setSlug] = useState(category?.slug || "")
  const [description, setDescription] = useState(category?.description || "")
  const [color, setColor] = useState(category?.color || "#3b82f6")

  const saving = create.isPending || update.isPending

  function autoSlug(t: string) {
    return t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
  }

  async function handleSave() {
    const data = { name, slug: slug || autoSlug(name), description: description || undefined, color }
    if (isEdit) {
      await update.mutateAsync({ id: category!.id, data })
    } else {
      await create.mutateAsync(data)
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">{isEdit ? "Editar Categoria" : "Nova Categoria"}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium">Nome *</label>
            <Input className="mt-1" value={name} onChange={e => { setName(e.target.value); if (!isEdit) setSlug(autoSlug(e.target.value)) }} placeholder="Nome da categoria" />
          </div>
          <div>
            <label className="text-sm font-medium">Slug</label>
            <Input className="mt-1" value={slug} onChange={e => setSlug(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">Descrição</label>
            <textarea className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-background resize-none" rows={2} value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrição da categoria" />
          </div>
          <div>
            <label className="text-sm font-medium">Cor</label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" className="w-10 h-9 rounded border cursor-pointer" value={color} onChange={e => setColor(e.target.value)} />
              <Input className="flex-1" value={color} onChange={e => setColor(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            {isEdit ? "Salvar" : "Criar"}
          </Button>
        </div>
      </div>
    </div>
  )
}
